import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, ShoppingBag, Check } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export const FrequentlyBoughtTogether = ({ product }) => {
  const { add, setIsOpen, setCouponCode } = useCart();
  const [pairs, setPairs] = useState([]);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    setPairs([]);
    setChecked({});
    api.get(`/products/${product.id}/bought-together`).then((r) => {
      const list = (r.data || []).slice(0, 2);
      setPairs(list);
      setChecked(Object.fromEntries(list.map((p) => [p.id, true])));
    }).catch(() => {});
  }, [product.id]);

  if (!pairs.length) return null;

  const selected = pairs.filter((p) => checked[p.id]);
  const fullSet = selected.length === pairs.length;
  const total = product.price + selected.reduce((s, p) => s + p.price, 0);
  const bundleTotal = Math.round(total * 0.95 * 100) / 100;

  const addAll = () => {
    add(product, 1);
    selected.forEach((p) => add(p, 1));
    api.post("/analytics/bundle-add", { anchor_id: product.id, pair_ids: selected.map((p) => p.id) }).catch(() => {});
    if (fullSet) {
      setCouponCode("BUNDLE5");
      toast.success(`Added ${selected.length + 1} items — extra 5% off auto-applied at checkout`);
    } else {
      toast.success(`Added ${selected.length + 1} item${selected.length ? "s" : ""} to your bag`);
    }
    setIsOpen(true);
  };

  const MiniCard = ({ p, selectable }) => (
    <button
      type="button"
      onClick={selectable ? () => setChecked((c) => ({ ...c, [p.id]: !c[p.id] })) : undefined}
      data-testid={selectable ? `fbt-toggle-${p.id}` : "fbt-current"}
      className={`relative w-36 md:w-44 shrink-0 text-left transition-opacity duration-300 ${selectable && !checked[p.id] ? "opacity-40" : ""}`}
    >
      {selectable && (
        <span className={`absolute top-2 right-2 z-10 w-5 h-5 flex items-center justify-center border transition-colors duration-200 ${checked[p.id] ? "bg-gold border-gold text-white" : "bg-white border-zinc-300 text-transparent"}`}>
          <Check size={12} />
        </span>
      )}
      <div className="bg-[#faf7f7] border border-zinc-100 p-4">
        {p.image && <img src={p.image} alt={p.name} loading="lazy" className="w-full aspect-square object-contain" />}
      </div>
      <p className="mt-2.5 text-xs text-zinc-700 leading-snug line-clamp-2">{p.name}</p>
      <p className="mt-1 text-sm text-gold font-medium">{inr(p.price)}</p>
    </button>
  );

  return (
    <section className="py-20 border-t border-zinc-200 bg-white" data-testid="fbt-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="text-xs uppercase tracking-[0.5em] text-gold mb-3">Complete the Ritual</p>
          <h2 className="font-serif font-medium text-3xl sm:text-4xl text-zinc-900 tracking-tight mb-10">
            Frequently <em className="text-gold">Bought Together</em>
          </h2>
          <div className="flex flex-col lg:flex-row lg:items-center gap-10">
            <div className="flex items-start gap-3 md:gap-5 overflow-x-auto pb-2">
              <MiniCard p={product} selectable={false} />
              {pairs.map((p) => (
                <div key={p.id} className="flex items-start gap-3 md:gap-5 shrink-0">
                  <span className="mt-16 text-zinc-300"><Plus size={18} /></span>
                  <MiniCard p={p} selectable />
                </div>
              ))}
            </div>
            <div className="lg:ml-auto lg:pl-10 lg:border-l lg:border-zinc-200 shrink-0">
              <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                Total for {selected.length + 1} item{selected.length ? "s" : ""}
              </p>
              <div className="flex items-baseline gap-3 mt-2">
                <p className={`font-serif text-4xl ${fullSet ? "text-zinc-400 line-through text-3xl" : "text-zinc-900"}`} data-testid="fbt-total">{inr(total)}</p>
                {fullSet && <p className="font-serif text-4xl text-gold" data-testid="fbt-bundle-total">{inr(bundleTotal)}</p>}
              </div>
              {fullSet && (
                <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-600 mt-2" data-testid="fbt-bundle-note">
                  Extra 5% off with code BUNDLE5 — auto-applied
                </p>
              )}
              <button
                onClick={addAll}
                data-testid="fbt-add-all"
                className="mt-5 flex items-center justify-center gap-3 bg-gold text-white text-xs uppercase tracking-[0.3em] px-8 py-4 hover:bg-champagne transition-colors duration-300 w-full lg:w-auto"
              >
                <ShoppingBag size={15} /> Add All to Bag
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
